import React from 'react';
import styled from 'styled-components';
import FacebookApi, { FacebookResponseTypes } from '../../api/facebook';
import AppSettings from '../../settings';
import { Container } from '../../styled-components/Layout';
import { Select } from '../../styled-components';

const FacebookTestContainer = styled(Container)`
  align-items: middle;
  justify-content: baseline;
  padding: 1rem;
  flex-direction: column;
  overflow-y: scroll;
`;

const Group = styled.div`
  display: flex;
  justify-content: baseline;
  align-items: ${props => (props.vertical ? 'flex-start' : 'center')};
  margin: 0.5rem;
  flex-direction: ${props => (props.vertical ? 'column' : 'row')};
`;

const Button = styled.button`
  font-family: Helvetica, Arial, sans-serif;
  font-size: 13px;
  text-align: center;
  color: #fff;
  background-color: #4267b2;
  border-radius: 3px;
  height: 28px;
  padding: 2px 10px;
  cursor: pointer;
  text-decoration: none;
  display: block;
  margin-right: 0.5rem;
`;

const FieldSet = styled.fieldset`
  margin-bottom: 1rem;
`;

const TextBox = styled.input`
  height: 28px;
  margin-right: 0.5rem;
`;

const CheckBoxLabel = styled.label`
  margin-right: 0.5rem;
`;

const Link = styled.a`
  color: blue;
`;

class FacebookApiTest extends React.PureComponent {
  constructor() {
    super();

    this.facebookApi = {};

    this.state = {
      permissions: ['manage_pages', 'publish_pages'],
      imgFilePath: '',
      imgCaption: '',
      selectedPage: '',
      vidFilePath: '',
      vidTitle: '',
      vidDescription: '',
      authResponse: {},
      imageResponse: {},
      videoResponse: {},
      vidAsync: false,
      user: null
    };
  }

  componentDidMount = () => {
    this.facebookApi = new FacebookApi(AppSettings.credentialManagerPath, this.onResponse);
  };

  onResponse = response => {
    if (response.type === FacebookResponseTypes.AUTH) {
      if (response.error) {
        this.setState({ authResponse: response.data });
      } else {
        this.setState({
          user: response.data,
          selectedPage: response.data && response.data.pages ? response.data.pages[0].i: ''
        });
      }
    } else if (response.type === FacebookResponseTypes.IMAGE_UPLOAD) {
      this.setState({ imageResponse: response.error || response.data });
    } else if (response.type === FacebookResponseTypes.VIDEO_UPLOAD) {
      this.setState({ videoResponse: response.error || response.data });
    }
  };

  render() {
    const {
      permissions,
      authResponse,
      imageResponse,
      videoResponse,
      imgFilePath,
      imgCaption,
      vidFilePath,
      vidTitle,
      vidDescription,
      vidAsync,
      user
    } = this.state;

    return (
      <FacebookTestContainer>
        <h1>Facebook API Test</h1>
        <FieldSet>
          <legend>User Information:</legend>
          <Group vertical>
            <div>Logged in: {user ? 'Yes' : 'No'}</div>
            <div>
              {user ? (
                <div>
                  <pre>{JSON.stringify(user, null, 2)}</pre>
                </div>
              ) : (
                '{}'
              )}
            </div>
          </Group>
        </FieldSet>
        <FieldSet>
          <legend>Authentication:</legend>
          <Group>
            <Button
              onClick={() =>
                this.facebookApi
                  .loginDialog(permissions)
                  .then(() => this.setState({ authResponse: {} }))
                  .catch(error => this.setState({ authResponse: error }))
              }
            >
              Login Popup
            </Button>
            <Button
              onClick={() => {
                this.facebookApi.logout();
                this.setState({ authResponse: {} });
              }}
            >
              Logout
            </Button>
            <Button
              onClick={() => {
                this.facebookApi
                  .revokeLogin()
                  .then(() => this.setState({ authResponse: {} }))
                  .catch(error => this.setState({ authResponse: error }));
                this.setState({ authResponse: {} });
              }}
            >
              Revoke Login
            </Button>
            <Button
              onClick={() =>
                this.facebookApi
                  .loginDialog(permissions, true)
                  .then(() => this.setState({ authResponse: {} }))
                  .catch(error => this.setState({ authResponse: error }))
              }
            >
              Re-request Permissions
            </Button>
            <label htmlFor="permissions">
              <Link href="https://developers.facebook.com/docs/facebook-login/permissions/">
                Permissions
              </Link>{' '}
              (comma delimited):{' '}
            </label>
            <TextBox
              id="permissions"
              type="text"
              placeholder="Permissions"
              value={permissions.join(',')}
              onChange={e => this.setState({ permissions: e.target.value.split(',').map(p => p) })}
            />
          </Group>
          <div>
            Response:
            <div>
              <pre>{JSON.stringify(authResponse, null, 2)}</pre>
            </div>
          </div>
        </FieldSet>
        <FieldSet>
          <legend>Managed Pages</legend>
          <Group>
            <Select
              id="page-select"
              onChange={event => this.setState({ selectedPage: event.target.value })}
              value={this.state.selectedPage}
            >
              {this.state.user && this.state.user.pages
                ? this.state.user.pages.map(page => (
                  <option key={page.id} value={page.id}>
                    {page.name}
                  </option>
                  ))
                : ''}
            </Select>
          </Group>
        </FieldSet>
        <FieldSet>
          <legend>Image Publishing:</legend>
          <Group>
            <Button
              onClick={() =>
                this.facebookApi
                  .uploadImage(imgFilePath, imgCaption, this.state.selectedPage)
                  .then(r => this.setState({ imageResponse: r }))
                  .catch(err => {
                    if (err.message) {
                      return this.setState({ imageResponse: err.message });
                    }
                    this.setState({ imageResponse: err });
                  })
              }
            >
              Publish Image
            </Button>
            <TextBox
              type="text"
              placeholder="Caption"
              value={imgCaption}
              onChange={e => this.setState({ imgCaption: e.target.value })}
            />
            <TextBox
              type="text"
              placeholder="File Path (or data uri)"
              value={imgFilePath}
              onChange={e => this.setState({ imgFilePath: e.target.value })}
            />
          </Group>
          <div>
            Response:
            <div>
              <pre>{JSON.stringify(imageResponse, null, 2)}</pre>
            </div>
          </div>
        </FieldSet>
        <FieldSet>
          <legend>Video Publishing:</legend>
          <Group>
            <Button
              onClick={() =>
                this.facebookApi
                  .uploadVideo(
                    vidFilePath,
                    vidTitle,
                    vidDescription,
                    this.state.selectedPage,
                    vidAsync
                  )
                  .then(r => this.setState({ videoResponse: r }))
                  .catch(err => {
                    if (err.message) {
                      return this.setState({ videoResponse: err.message });
                    }
                    this.setState({ videoResponse: err });
                  })
              }
            >
              Publish Video
            </Button>
            <TextBox
              type="text"
              placeholder="Title"
              value={vidTitle}
              onChange={e => this.setState({ vidTitle: e.target.value })}
            />
            <TextBox
              type="text"
              placeholder="Description"
              value={vidDescription}
              onChange={e => this.setState({ vidDescription: e.target.value })}
            />
            <TextBox
              type="text"
              placeholder="File Path"
              value={vidFilePath}
              onChange={e => this.setState({ vidFilePath: e.target.value })}
            />
            <input
              id="vidAsync"
              type="checkbox"
              onClick={e => this.setState({ vidAsync: e.target.checked })}
            />
            <CheckBoxLabel htmlFor="vidAsync">Async?</CheckBoxLabel>
          </Group>
          <div>
            Response:
            <div>
              <pre>{JSON.stringify(videoResponse, null, 2)}</pre>
            </div>
          </div>
        </FieldSet>
      </FacebookTestContainer>
    );
  }
}

export default FacebookApiTest;



// WEBPACK FOOTER //
// ./app/components/Test/FacebookApiTest.js